import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { changePassword, changeUsername } from '../api/placeholder';
import { Lock, User, LogOut, Eye, EyeOff } from 'lucide-react';
import ConfirmDialog from '../components/ConfirmDialog';

export default function Settings() {
  const { user, logout, updatePassword, updateUsername } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Username change state
  const [currentUsernameInput, setCurrentUsernameInput] = useState('');
  const [newUsernameInput, setNewUsernameInput] = useState('');
  const [usernameMessage, setUsernameMessage] = useState(null);
  const [savingUsername, setSavingUsername] = useState(false);

  const handleChangeUsername = async (e) => {
    e.preventDefault();
    setUsernameMessage(null);

    if (!currentUsernameInput || !newUsernameInput) {
      setUsernameMessage({ type: 'error', text: 'Please fill in all fields' });
      return;
    }

    // Verify current username matches logged-in user
    if (currentUsernameInput !== user.username) {
      setUsernameMessage({ type: 'error', text: 'Current username is incorrect' });
      return;
    }

    if (newUsernameInput.length < 3) {
      setUsernameMessage({ type: 'error', text: 'Username must be at least 3 characters' });
      return;
    }

    setSavingUsername(true);

    try {
      await changeUsername(currentUsernameInput, newUsernameInput);
      updateUsername(newUsernameInput);
      setUsernameMessage({ type: 'success', text: 'Username changed successfully' });
      setCurrentUsernameInput('');
      setNewUsernameInput('');
    } catch (err) {
      setUsernameMessage({ type: 'error', text: 'Failed to change username. Please try again.' });
    } finally {
      setSavingUsername(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setMessage(null);

    if (!currentPassword || !newPassword || !confirmPassword) {
      setMessage({ type: 'error', text: 'Please fill in all fields' });
      return;
    }
    if (newPassword !== confirmPassword) {
      setMessage({ type: 'error', text: 'New passwords do not match' });
      return;
    }
    if (newPassword.length < 6) {
      setMessage({ type: 'error', text: 'Password must be at least 6 characters' });
      return;
    }

    setSaving(true);

    try {
      // Verify current password
      if (currentPassword !== user.password) {
        setMessage({ type: 'error', text: 'Current password is incorrect' });
        setSaving(false);
        return;
      }

      // Send to API
      await changePassword(user.username, newPassword);

      // Update stored password in context
      updatePassword(newPassword);

      setMessage({ type: 'success', text: 'Password changed successfully' });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to change password. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    setShowLogoutConfirm(false);
    logout();
  };

  const inputClass =
    'w-full rounded-xl border border-surface-600 bg-surface-700 px-3 py-2.5 text-sm text-slate-200 placeholder:text-slate-500 focus:border-neon-cyan/50 focus:outline-none focus:ring-2 focus:ring-neon-cyan/20 transition-all';

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-100">Settings</h1>
        <p className="text-sm text-slate-500">
          Manage your admin account settings
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Admin Info */}
        <div className="rounded-2xl border border-surface-600 bg-surface-800 p-6">
          <div className="mb-4 flex items-center gap-2 text-neon-cyan">
            <User size={18} />
            <h2 className="text-sm font-semibold">Admin Information</h2>
          </div>

          <div className="space-y-4">
            <div>
              <label className="text-xs font-medium text-slate-500">Name</label>
              <p className="mt-0.5 text-sm text-slate-200">{user?.name}</p>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500">Username</label>
              <p className="mt-0.5 text-sm text-slate-200">{user?.username}</p>
            </div>
          </div>
        </div>

        {/* Change Password */}
        <div className="rounded-2xl border border-surface-600 bg-surface-800 p-6">
          <div className="mb-4 flex items-center gap-2 text-neon-cyan">
            <Lock size={18} />
            <h2 className="text-sm font-semibold">Change Password</h2>
          </div>

          {message && (
            <div
              className={`mb-4 rounded-xl px-4 py-3 text-sm border ${
                message.type === 'success'
                  ? 'bg-neon-green/10 text-neon-green border-neon-green/30'
                  : 'bg-neon-pink/10 text-neon-pink border-neon-pink/30'
              }`}
            >
              {message.text}
            </div>
          )}

          <form onSubmit={handleChangePassword} className="space-y-4">
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-400">
                Current Password
              </label>
              <div className="relative">
                <input
                  type={showCurrentPassword ? 'text' : 'password'}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className={`${inputClass} pr-10`}
                  placeholder="Enter current password"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
                >
                  {showCurrentPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-400">
                New Password
              </label>
              <div className="relative">
                <input
                  type={showNewPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className={`${inputClass} pr-10`}
                  placeholder="Enter new password"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
                >
                  {showNewPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-400">
                Confirm New Password
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className={`${inputClass} pr-10`}
                  placeholder="Confirm new password"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
                >
                  {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <button
              type="submit"
              disabled={saving}
              className="rounded-xl bg-neon-cyan/20 border border-neon-cyan/40 px-5 py-2.5 text-sm font-semibold text-neon-cyan hover:bg-neon-cyan/30 hover:shadow-neon-cyan transition-all focus:outline-none focus:ring-2 focus:ring-neon-cyan/30 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? (
                <span className="flex items-center gap-2">
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-neon-cyan border-t-transparent" />
                  Updating...
                </span>
              ) : (
                'Update Password'
              )}
            </button>
          </form>
        </div>

        {/* Change Username */}
        <div className="rounded-2xl border border-surface-600 bg-surface-800 p-6 lg:col-span-2">
          <div className="mb-4 flex items-center gap-2 text-neon-cyan">
            <User size={18} />
            <h2 className="text-sm font-semibold">Change Username</h2>
          </div>

          {usernameMessage && (
            <div
              className={`mb-4 rounded-xl px-4 py-3 text-sm border ${
                usernameMessage.type === 'success'
                  ? 'bg-neon-green/10 text-neon-green border-neon-green/30'
                  : 'bg-neon-pink/10 text-neon-pink border-neon-pink/30'
              }`}
            >
              {usernameMessage.text}
            </div>
          )}

          <form onSubmit={handleChangeUsername} className="space-y-4 max-w-md">
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-400">
                Current Username
              </label>
              <input
                type="text"
                value={currentUsernameInput}
                onChange={(e) => setCurrentUsernameInput(e.target.value)}
                className={inputClass}
                placeholder="Enter current username"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-400">
                New Username
              </label>
              <input
                type="text"
                value={newUsernameInput}
                onChange={(e) => setNewUsernameInput(e.target.value)}
                className={inputClass}
                placeholder="Enter new username"
              />
            </div>
            <button
              type="submit"
              disabled={savingUsername}
              className="rounded-xl bg-neon-cyan/20 border border-neon-cyan/40 px-5 py-2.5 text-sm font-semibold text-neon-cyan hover:bg-neon-cyan/30 hover:shadow-neon-cyan transition-all focus:outline-none focus:ring-2 focus:ring-neon-cyan/30 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {savingUsername ? (
                <span className="flex items-center gap-2">
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-neon-cyan border-t-transparent" />
                  Updating...
                </span>
              ) : (
                'Update Username'
              )}
            </button>
          </form>
        </div>

        {/* Logout */}
        <div className="rounded-2xl border border-surface-600 bg-surface-800 p-6 lg:col-span-2">
          <div className="mb-3 flex items-center gap-2 text-neon-pink">
            <LogOut size={18} />
            <h2 className="text-sm font-semibold">Session</h2>
          </div>
          <p className="mb-4 text-sm text-slate-500">
            End your current admin session and return to the login screen.
          </p>
          <button
            onClick={() => setShowLogoutConfirm(true)}
            className="rounded-xl bg-neon-pink/20 border border-neon-pink/40 px-5 py-2.5 text-sm font-semibold text-neon-pink hover:bg-neon-pink/30 hover:shadow-neon-pink transition-all focus:outline-none focus:ring-2 focus:ring-neon-pink/30"
          >
            Sign Out
          </button>
        </div>
      </div>

      <ConfirmDialog
        open={showLogoutConfirm}
        title="Sign Out"
        message="Are you sure you want to sign out of the admin dashboard?"
        confirmLabel="Sign Out"
        cancelLabel="Cancel"
        variant="danger"
        onConfirm={handleLogout}
        onCancel={() => setShowLogoutConfirm(false)}
      />
    </div>
  );
}
